import type { FacetOption } from "@/lib/types/nav"
import type { FacetValue, SearchFacet } from "@/lib/types/search"

const CATEGORY_FACET_KEY = "category"

export const attributeFacets = (facets: SearchFacet[] | undefined): SearchFacet[] | undefined => facets?.filter((facet) => facet.key !== CATEGORY_FACET_KEY)

const categoryValues = (facets: SearchFacet[] | undefined): FacetValue[] => facets?.find((facet) => facet.key === CATEGORY_FACET_KEY)?.values ?? []

export const categoryFacetLabels = (facets: SearchFacet[] | undefined): Record<string, string> =>
  Object.fromEntries(categoryValues(facets).map((value) => [value.value, value.label]))

const selectedPath = (selected: string[], curatedParent: Map<string, string>, curated: Set<string>, values: Map<string, FacetValue>): Set<string> => {
  const path = new Set<string>()
  for (const id of selected) {
    let current: string | undefined = id
    while (current !== undefined && !path.has(current)) {
      path.add(current)
      current = curated.has(current) ? curatedParent.get(current) : values.get(current)?.parent
    }
  }
  return path
}

export const categoryTreeWithResults = (options: FacetOption[], facets: SearchFacet[] | undefined, selected: string[]): FacetOption[] => {
  const values = categoryValues(facets)
  if (values.length === 0) return options

  const byValue = new Map(values.map((value) => [value.value, value]))
  const curatedParent = new Map(options.flatMap((department) => (department.children ?? []).map((child) => [child.value, department.value] as const)))
  const curated = new Set([...options.map((department) => department.value), ...curatedParent.keys()])
  const path = selectedPath(selected, curatedParent, curated, byValue)

  const withDeeper = (option: FacetOption): FacetOption => {
    if (!path.has(option.value)) return option
    const children = values
      .filter((value) => value.parent === option.value && !curated.has(value.value))
      .map((value) => withDeeper({ value: value.value, label: value.label, handle: value.handle, ...(value.handlePath ? { path: value.handlePath } : {}), count: value.count }))
    return children.length > 0 ? { ...option, children } : option
  }

  return options.map((department) => {
    const children = (department.children ?? []).map((child) => withDeeper({ ...child, count: byValue.get(child.value)?.count ?? 0 }))
    return { ...department, count: byValue.get(department.value)?.count ?? 0, ...(children.length > 0 ? { children } : {}) }
  })
}

const containsAny = (option: FacetOption, ids: string[]): boolean => ids.includes(option.value) || (option.children ?? []).some((child) => containsAny(child, ids))

const withChildren = (option: FacetOption, children: FacetOption[]): FacetOption => {
  const { children: _allChildren, ...category } = option
  return children.length > 0 ? { ...category, children } : category
}

const offeredSubcategories = (options: FacetOption[], selected: string[]): FacetOption[] =>
  options.flatMap((option) => {
    const children = offeredSubcategories(option.children ?? [], selected)
    const inResults = option.count === undefined || option.count > 0
    if (!inResults && children.length === 0 && !containsAny(option, selected)) return []
    return [withChildren(option, children)]
  })

export const offeredCategories = (options: FacetOption[], selected: string[]): FacetOption[] =>
  options.map((department) => withChildren(department, offeredSubcategories(department.children ?? [], selected)))
